import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import cn from 'classnames'

import i18n from 'i18n'
import DropDown from 'components/Shared/DropDown'
import { TransparentBgButton } from 'components/Shared/Button'
import ErrorMessage from 'components/Shared/ErrorMessage'
import authApi from 'api/auth'
import { ForwardIcon, WorldIcon, LinkChainIcon } from 'images'

const SHARE_MODES = [
  {
    value: 'preview',
    scopes: ['setup_process:read'],
    labelKey: 'Recommendations.summary.share.modes.readOnly',
  },
  {
    value: 'boost',
    scopes: ['setup_process:boost'],
    labelKey: 'Recommendations.summary.share.modes.readAndPublish',
  },
  {
    value: 'editAndBoost',
    scopes: ['setup_process:write', 'setup_process:boost'],
    labelKey: 'Recommendations.summary.share.modes.edit',
  },
]

const DEFAULT_MODE = 'preview'

const ShareSummaryItem = ({ eventId }) => {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(false)
  const [selectedMode, setSelectedMode] = useState(null)
  const [link, setLink] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [copied, setCopied] = useState(false)

  const generateLink = useCallback(
    (mode) => {
      const modeConfig = SHARE_MODES.find((m) => m.value === mode)
      setLoading(true)
      setError(null)
      authApi
        .getAnonymousAuthCode(modeConfig.scopes, eventId)
        .then((response) => {
          if (response.error) {
            setError(t('common.genericError'))
          } else {
            setLink(
              `${window.location.origin}/${i18n.language}/anon-setup/${eventId}?code=${response.data.code}`,
            )
          }
        })
        .catch(() => setError(t('common.genericError')))
        .finally(() => setLoading(false))
    },
    [eventId, t],
  )

  const handleToggle = () => {
    setExpanded((wasExpanded) => {
      if (!wasExpanded && !selectedMode) {
        setSelectedMode(DEFAULT_MODE)
        generateLink(DEFAULT_MODE)
      }
      return !wasExpanded
    })
  }

  const handleModeChange = (mode) => {
    setSelectedMode(mode)
    generateLink(mode)
  }

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(link)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }, [link])

  return (
    <div className="card card-no-padding-mobile rounded overflow-visible summary_share">
      <div
        className="d-flex align-items-center summary_share_header"
        onClick={handleToggle}
        role="button"
        tabIndex={0}
      >
        <div className="summary_item summary_item_icon">
          <ForwardIcon width={24} height={24} />
        </div>
        <p className="summary_item summary_item_content flex-grow-1 mb-0">
          <strong>{t('Recommendations.summary.share.title')}</strong>
          <span>{t('Recommendations.summary.share.description')}</span>
        </p>
        <div className={cn('accordion-arrow', { open: expanded })} />
      </div>

      {expanded && (
        <>
          <div className="summary_share_mode">
            <WorldIcon className="summary_share_mode_icon" width={16} height={16} />
            <span>{t('Recommendations.summary.share.anyoneWithLink')}</span>
            <DropDown
              classNames="summary_share_dropdown"
              value={selectedMode}
              required={false}
              options={SHARE_MODES.map((mode) => ({ value: mode.value, text: mode.labelKey }))}
              onChange={handleModeChange}
            />
          </div>

          <p className="summary_share_validity">{t('Recommendations.summary.share.validityMsg')}</p>

          {error && <ErrorMessage danger>{error}</ErrorMessage>}

          {link && !loading && (
            <div className="summary_share_link">
              <div className="summary_share_link_input">
                <LinkChainIcon width={16} height={16} />
                <input type="text" readOnly value={link} onFocus={(e) => e.target.select()} />
              </div>
              <TransparentBgButton
                text={copied ? t('common.copied') : t('common.copy')}
                onClick={handleCopy}
                color="navy"
                fullWidth={false}
                disabled={copied}
                disabledTitle=""
              />
            </div>
          )}
        </>
      )}
    </div>
  )
}

ShareSummaryItem.propTypes = {
  eventId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
}

export default ShareSummaryItem
